import React, { useState } from "react";
import styles from "./CustomizedField.module.css";
import { TextField, Button, FormControlLabel, Checkbox } from "@mui/material";
import deleteImage from "./Images/delete.svg";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import CancelBtn from "../../../../../../../../components/Buttons/CancelBtn";
import SaveBtn from "../../../../../../../../components/Buttons/SaveBtn";
import EditIconBtn from "../../../../../../../../components/Buttons/EditIconBtn";
import DeleteBtn from "../../../../../../../../components/Buttons/DeleteBtn";
import DeletePopup from "../../../../../../../../components/CommonPopups/DeletePopup";

function CustomizedDropdownField(props: any) {
  const {
    field,
    onFieldChange,
    onDelete,
    isDropdownFieldFocused,
    setIsDropdownFieldFocused,
    leadsFromResponseChecked,
    edit = false,
    deleteAction = false,
    mandatory = false,
    isNameUniqueCheck,
  } = props;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>("");
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [answers, setAnswers] = useState<any[]>([]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleMandatoryChange = (e: any) => {
    const isMandatory = e.target.checked;
    onFieldChange({
      ...field,
      required: isMandatory,
    });
  };

  const handleQuestionChange = (e: any) => {
    setQuestion(e.target.value);
  };

  const generateId = (name: any) => {
    const withoutSpaces = name.replace(/\s+/g, "_").toLowerCase();
    return withoutSpaces;
  };

  const handleDeleteConfirmed = () => {
    setShowDeletePopup(false);
    onDelete(field);
  };

  const handleDeleteCancelled = () => {
    setShowDeletePopup(false);
  };

  const handleAddAnswer = () => {
    setAnswers([...answers, { answerId: "", answer: "", isValid: true }]);
  };

  // const handleAnswerChange = (index: number, value: string) => {
  //   setAnswers((prevAnswers) => {
  //     const newAnswers = [...prevAnswers];
  //     newAnswers[index].answer = value;
  //     return newAnswers;
  //   });
  // };

  const handleAnswerChange = (index: number, value: string) => {
    const updatedAnswers = answers.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          answer: value,
          answerId: value,
          isValid: true,
        };
      }
      return item;
    });

    const uniqueAnswers = new Set<string>();
    const finalAnswers = updatedAnswers.map((item) => {
      const isDuplicate = uniqueAnswers.has(item.answer.toLowerCase());
      uniqueAnswers.add(item.answer.toLowerCase());
      return { ...item, isValid: !isDuplicate || item.answer === "" };
    });

    setAnswers(finalAnswers);
  };

  const handleRemoveAnswerField = (index: number) => {
    setAnswers((prevAnswers) => prevAnswers.filter((_, i) => i !== index));
  };

  const capitalizeSentences = (text: string) => {
    let lowercasedText = text.toLowerCase();

    let capitalizedText = lowercasedText.replace(
      /(^\s*\w|[\.\!\?]\s*\w)/g,
      function (match) {
        return match.toUpperCase();
      }
    );

    return capitalizedText;
  };

  const hasEmptyAnswer = answers?.some((item) => item.answer === "");

  const handleCancelClick = () => {
    setIsEditing(false);
    setQuestion("");
  };

  const handleSaveClick = () => {
    const newOptions = answers.map((answer) => ({
      name: answer.answer,
      id: generateId(answer.answer),
    }));

    onFieldChange({
      ...field,
      fieldName: question,
      options: newOptions,
    });

    setIsEditing(false);
  };

  React.useEffect(() => {
    setAnswers(
      field?.options?.map((d: any) => {
        return { answerId: d?.id, answer: d?.name, isValid: true };
      })
    );
    setQuestion(field?.fieldName);
  }, [field, isEditing]);

  return (
    <div className={styles.customizedFieldWrapper}>
      <div className={styles.customizedFieldTop}>
        <div className={styles.label}>
          {capitalizeSentences(field.fieldName)}
        </div>
        {!isEditing && (
          <div className={styles.customizedFieldTopRight}>
            {edit && <EditIconBtn onClick={handleEditClick} />}
            {deleteAction && (
              <DeleteBtn
                icon={deleteImage}
                onClick={() => setShowDeletePopup(!showDeletePopup)}
              />
            )}
            {showDeletePopup && (
              <DeletePopup
                open={showDeletePopup}
                onClose={handleDeleteCancelled}
                noBtnClick={handleDeleteCancelled}
                yesBtnClick={handleDeleteConfirmed}
                title={"Are you sure you want to delete this question?"}
              />
            )}
          </div>
        )}
      </div>

      <TextField
        placeholder="Write a question"
        disabled={!isEditing}
        fullWidth
        variant="outlined"
        size="small"
        value={isEditing ? question : ""}
        onChange={handleQuestionChange}
        inputProps={{
          style: {
            padding: "8px 12px",
            backgroundColor: isEditing ? "#FFFFFF" : "#FAFAFA",
            borderRadius: "4px",
          },
        }}
        draggable={isDropdownFieldFocused === false}
        onFocus={() => {
          setIsDropdownFieldFocused(true);
        }}
        onBlur={() => {
          setIsDropdownFieldFocused(false);
        }}
        error={isEditing && !isNameUniqueCheck(question, field?.index)}
        helperText={
          isEditing && !isNameUniqueCheck(question, field?.index)
            ? "Field label already exist"
            : ""
        }
      />
      {mandatory && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              onChange={handleMandatoryChange}
              checked={field?.required}
              sx={{
                strokeWidth: 1,
                color: "#D0D5DD",
              }}
            />
          }
          label="Mandatory"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "13px",
              color: "#667085",
              lineHeight: "20px",
            },
          }}
        />
      )}

      {isEditing ? (
        <div>
          {answers?.map((item, index) => (
            <TextField
              fullWidth
              key={`answer-${index}`}
              variant="outlined"
              size="small"
              value={item.answer}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              InputProps={{
                endAdornment: (
                  <ClearIcon
                    fontSize="small"
                    style={{
                      cursor: "pointer",
                      color: "#667085",
                    }}
                    onClick={() => handleRemoveAnswerField(index)}
                  />
                ),
                style: {
                  backgroundColor: isEditing ? "#FFFFFF" : "#FAFAFA",
                  borderRadius: "4px",
                },
              }}
              sx={{
                "&.MuiTextField-root": {
                  marginTop: "12px",
                },
              }}
              error={!item.isValid}
              helperText={!item.isValid ? "Answer already exist" : ""}
            />
          ))}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Button
              component="label"
              sx={{
                textTransform: "capitalize",
                fontWeight: "500",
                display: "flex",
              }}
              startIcon={<AddIcon />}
              onClick={handleAddAnswer}
            >
              Add an answer
            </Button>

            <div className={styles.btnsWrap}>
              <CancelBtn onClick={handleCancelClick} />
              <SaveBtn
                onClick={handleSaveClick}
                disabled={
                  !question.trim() ||
                  !isNameUniqueCheck(question, field?.index) ||
                  answers?.length === 0 ||
                  hasEmptyAnswer ||
                  answers?.filter((item: any) => item.isValid === false)
                    ?.length > 0
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
export default CustomizedDropdownField;

// import React, { useState, useEffect, useRef } from "react";
// import styles from "./CustomizedField.module.css";
// import DeletePopup from "../../../../../../../../components/CommonPopups/DeletePopup";
// import deleteImage from "./Images/delete.svg";
// import DeleteBtn from "../../../../../../../../components/Buttons/DeleteBtn";
// import EditIconBtn from "../../../../../../../../components/Buttons/EditIconBtn";

// function CustomizedDropdownField(props: any) {
//   const {
//     field,
//     onFieldChange,
//     isDropdownFieldFocused,
//     setIsDropdownFieldFocused,
//     deleteAction = false,
//     onDelete,
//     edit = false,
//   } = props;

//   const [searchTerm, setSearchTerm] = useState("");
//   const [filteredOptions, setFilteredOptions] = useState(field?.options || []);
//   const dropdownRef = useRef<HTMLDivElement>(null);
//   const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
//   const [isEditing, setIsEditing] = useState<boolean>(false);

//   // 🔍 Filter options as user types
//   useEffect(() => {
//     if (!searchTerm.trim()) {
//       setFilteredOptions(field?.options || []);
//     } else {
//       const filtered = (field?.options || []).filter((opt: any) =>
//         opt.name.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//       setFilteredOptions(filtered);
//     }
//   }, [searchTerm, field?.options]);

//   // 🧩 Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (
//         dropdownRef.current &&
//         !dropdownRef.current.contains(event.target as Node)
//       ) {
//         setIsDropdownFieldFocused(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [setIsDropdownFieldFocused]);

//   const capitalizeSentences = (text: string) => {
//     if (!text) return "";
//     return text.charAt(0).toUpperCase() + text.slice(1);
//   };

//   const handleDeleteConfirmed = () => {
//     setShowDeletePopup(false);
//     onDelete(field);
//   };

//   const handleEditClick = () => {
//     setIsEditing(true);
//   };

//   const handleDeleteCancelled = () => {
//     setShowDeletePopup(false);
//   };

//   return (
//     <div
//       ref={dropdownRef}
//       className={styles.customizedFieldWrapper}
//       style={{
//         display: "flex",
//         flexDirection: "column",
//         gap: "6px",
//         position: "relative",
//       }}
//     >
//       {/* === Label === */}
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//         }}
//       >
//         <div
//           style={{
//             fontSize: "14px",
//             fontWeight: 500,
//             color: "#344054",
//           }}
//         >
//           {capitalizeSentences(field?.fieldName || "Select Option")}
//         </div>

//         {/* {deleteAction && (
//           <>
//             <img
//               src={deleteImage}
//               alt="delete"
//               style={{
//                 width: "16px",
//                 height: "16px",
//                 cursor: "pointer",
//                 opacity: 0.8,
//                 transition: "opacity 0.2s",
//               }}
//               onClick={() => setShowDeletePopup(true)}
//               onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
//               onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
//             />

//             {showDeletePopup && (
//               <DeletePopup
//                 open={showDeletePopup}
//                 onClose={() => setShowDeletePopup(false)}
//                 noBtnClick={() => setShowDeletePopup(false)}
//                 yesBtnClick={() => {
//                   handleDeleteConfirmed();
//                   setShowDeletePopup(false);
//                 }}
//                 title={"Are you sure you want to delete this field?"}
//               />
//             )}
//           </>
//         )} */}
//         {!isEditing && (
//           <div className={styles.customizedFieldTopRight}>
//             {deleteAction && (
//               <DeleteBtn
//                 icon={deleteImage}
//                 onClick={() => setShowDeletePopup(!showDeletePopup)}
//               />
//             )}
//             {showDeletePopup && (
//               <DeletePopup
//                 open={showDeletePopup}
//                 onClose={handleDeleteCancelled}
//                 noBtnClick={handleDeleteCancelled}
//                 yesBtnClick={handleDeleteConfirmed}
//                 title={"Are you sure you want to delete this question?"}
//               />
//             )}
//           </div>
//         )}
//       </div>

//       {/* === Searchable Dropdown Box === */}
//       <div
//         style={{
//           position: "relative",
//           border: "1px solid #E4E7EC",
//           borderRadius: "8px",
//           backgroundColor: "#FFFFFF",
//           display: "flex",
//           alignItems: "center",
//           height: "40px",
//           padding: "0 12px",
//           gap: "8px",
//           transition: "all 0.2s ease",
//           ...(isDropdownFieldFocused && {
//             borderColor: "#11C89B",
//             boxShadow: "0 0 0 2px rgba(17, 200, 155, 0.15)",
//           }),
//         }}
//         draggable={!isDropdownFieldFocused}
//         onClick={() => {
//           if (!isDropdownFieldFocused) {
//             setIsDropdownFieldFocused(true);
//           }
//         }}
//       >
//         {/* 🔍 Icon */}
//         <svg
//           xmlns="http://www.w3.org/2000/svg"
//           fill="none"
//           viewBox="0 0 24 24"
//           strokeWidth={2}
//           stroke="currentColor"
//           style={{
//             width: "16px",
//             height: "16px",
//             color: "#98A2B3",
//           }}
//         >
//           <path
//             strokeLinecap="round"
//             strokeLinejoin="round"
//             d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1110.65 6.65a7.5 7.5 0 016 10.0z"
//           />
//         </svg>

//         {/* Searchable Input */}
//         <input
//           type="text"
//           value={
//             isDropdownFieldFocused ? searchTerm : field?.selectedOption || ""
//           }
//           onChange={(e) => setSearchTerm(e.target.value)}
//           placeholder="Search blocks..."
//           onFocus={() => setIsDropdownFieldFocused(true)}
//           style={{
//             flexGrow: 1,
//             fontSize: "14px",
//             color: "#101828",
//             border: "none",
//             outline: "none",
//             backgroundColor: "transparent",
//           }}
//           disabled={edit === false}
//         />

//         {/* ▼ Dropdown Arrow */}
//         <svg
//           xmlns="http://www.w3.org/2000/svg"
//           width="16"
//           height="16"
//           fill="none"
//           viewBox="0 0 24 24"
//           stroke="currentColor"
//           strokeWidth="2"
//           style={{
//             color: "#667085",
//             transform: isDropdownFieldFocused ? "rotate(180deg)" : "rotate(0)",
//             transition: "transform 0.2s ease",
//           }}
//         >
//           <path
//             strokeLinecap="round"
//             strokeLinejoin="round"
//             d="M19 9l-7 7-7-7"
//           />
//         </svg>
//       </div>

//       {/* === Dropdown Options === */}
//       {isDropdownFieldFocused && (
//         <div
//           style={{
//             position: "absolute",
//             top: "100%",
//             left: 0,
//             right: 0,
//             marginTop: "4px",
//             border: "1px solid #E4E7EC",
//             borderRadius: "8px",
//             backgroundColor: "#FFFFFF",
//             zIndex: 10,
//             boxShadow:
//               "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)",
//             overflow: "hidden",
//           }}
//         >
//           <div style={{ maxHeight: "200px", overflowY: "auto" }}>
//             {filteredOptions.length > 0 ? (
//               filteredOptions.map((opt: any, index: number) => (
//                 <div
//                   key={index}
//                   onClick={() => {
//                     onFieldChange({
//                       ...field,
//                       selectedOption: opt.name,
//                     });
//                     setSearchTerm("");
//                     setIsDropdownFieldFocused(false);
//                   }}
//                   style={{
//                     padding: "10px 12px",
//                     fontSize: "14px",
//                     color:
//                       field?.selectedOption === opt.name
//                         ? "#11C89B"
//                         : "#101828",
//                     backgroundColor:
//                       field?.selectedOption === opt.name
//                         ? "rgba(17, 200, 155, 0.08)"
//                         : "#FFFFFF",
//                     cursor: "pointer",
//                     transition: "background-color 0.2s ease",
//                   }}
//                   onMouseEnter={(e) =>
//                     (e.currentTarget.style.backgroundColor =
//                       "rgba(17, 200, 155, 0.08)")
//                   }
//                   onMouseLeave={(e) =>
//                     (e.currentTarget.style.backgroundColor =
//                       field?.selectedOption === opt.name
//                         ? "rgba(17, 200, 155, 0.08)"
//                         : "#FFFFFF")
//                   }
//                 >
//                   {opt.name}
//                 </div>
//               ))
//             ) : (
//               <div
//                 style={{
//                   padding: "10px 12px",
//                   fontSize: "14px",
//                   color: "#98A2B3",
//                 }}
//               >
//                 No results found
//               </div>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default CustomizedDropdownField;
